import { Injectable } from "@angular/core";
import { AppConfig } from "../config/app.config";
import CryptoJS from "crypto-js";

@Injectable({
  providedIn: "root",
})
export class SecureSessionStorageService {
  private static isPatched = false;
  private readonly secret = this.resolveSecret();

  init(): void {
    if (SecureSessionStorageService.isPatched || typeof window === "undefined") {
      return;
    }

    const encryptionSecret = this.secret;
    const storageProto = Object.getPrototypeOf(window.sessionStorage) as Storage;
    const nativeGetItem = storageProto.getItem;
    const nativeSetItem = storageProto.setItem;

    storageProto.setItem = function (key: string, value: string): void {
      if (this === window.sessionStorage) {
        const normalizedValue = String(value);
        const encryptedValue = SecureSessionStorageService.encryptValue(
          normalizedValue,
          encryptionSecret
        );
        nativeSetItem.call(this, key, encryptedValue);
        return;
      }

      nativeSetItem.call(this, key, value);
    };

    storageProto.getItem = function (key: string): string | null {
      const value = nativeGetItem.call(this, key);
      if (this !== window.sessionStorage || value === null) {
        return value;
      }

      return SecureSessionStorageService.decryptValue(value, encryptionSecret);
    };

    SecureSessionStorageService.isPatched = true;
  }

  private resolveSecret(): string {
    const configuredSecret = (AppConfig.storageSecret || "").trim();
    return configuredSecret.length > 0
      ? configuredSecret
      : "abacus-ui-session-storage-secret";
  }

  private static encryptValue(value: string, secret: string): string {
    return CryptoJS.AES.encrypt(value, secret).toString();
  }

  private static decryptValue(value: string, secret: string): string {
    try {
      const decrypted = CryptoJS.AES.decrypt(value, secret).toString(
        CryptoJS.enc.Utf8
      );

      if (decrypted !== "" || value === "") {
        return decrypted;
      }

      return SecureSessionStorageService.tryLegacyXorDecrypt(value, secret);
    } catch {
      return value;
    }
  }

  private static tryLegacyXorDecrypt(value: string, secret: string): string {
    try {
      const encrypted = SecureSessionStorageService.fromBase64(value);
      const secretBytes = new TextEncoder().encode(secret);
      const decrypted = encrypted.map(
        (byte, index) => byte ^ secretBytes[index % secretBytes.length]
      );

      return new TextDecoder().decode(decrypted);
    } catch {
      return value;
    }
  }

  private static fromBase64(value: string): Uint8Array {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

}
